/**
 * My tickets (spec §7 screen 4).
 *
 * "live progress (7/10 with 3 to go), history."
 *
 * The progress line is the reason people reopen the app mid-round, so it is the
 * largest thing on each row. A ticket still in play shows how many legs are
 * still to come, not just how many are already correct — "7/10, 3 to go" is a
 * live position; "7/10" alone reads like a final score.
 */

import { useEffect, useState } from 'react';
import { api, ApiError, type TicketDetail } from './api.js';
import { formatTzs, formatTzsExact, parseSenti } from './money.js';
import { S } from './ui.js';

/**
 * Tickets are recorded per round in the round's Durable Object, so a global
 * "my tickets" list needs a read projection that does not exist yet. Until it
 * does, the screen tracks locally-purchased ticket ids and fetches each — which
 * is honest about what it can show rather than rendering an empty list that
 * looks like "you have never played".
 */
const STORE_KEY = 'bongo.tickets';

export interface TicketRef {
  readonly ticketId: string;
  readonly roundId: string;
}

export function rememberTicket(ref: TicketRef): void {
  try {
    const existing = loadTicketRefs();
    if (existing.some((t) => t.ticketId === ref.ticketId)) return;
    localStorage.setItem(STORE_KEY, JSON.stringify([ref, ...existing].slice(0, 100)));
  } catch {
    /* storage unavailable; the ticket still exists server-side */
  }
}

export function loadTicketRefs(): readonly TicketRef[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TicketRef[]) : [];
  } catch {
    return [];
  }
}

export function MyTickets(): React.JSX.Element {
  const [tickets, setTickets] = useState<readonly TicketDetail[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      const refs = loadTicketRefs();
      if (refs.length === 0) {
        if (!cancelled) setTickets([]);
        return;
      }
      try {
        const loaded = await Promise.all(
          refs.map((r) => api.getTicket(r.ticketId, r.roundId).catch(() => null)),
        );
        if (!cancelled) setTickets(loaded.filter((t): t is TicketDetail => t !== null));
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Imeshindikana kupakia.');
      }
    };
    void load();
    // Progress moves as results land, so refresh while the screen is open.
    const id = setInterval(() => void load(), 15_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (error !== null) {
    return (
      <main style={S.page}>
        <p style={S.error}>{error}</p>
      </main>
    );
  }

  if (tickets === null) {
    return (
      <main style={S.page}>
        <p style={S.muted}>Inapakia…</p>
      </main>
    );
  }

  return (
    <main style={S.page}>
      <h1 style={S.h1}>Tiketi zangu</h1>

      {tickets.length === 0 && (
        <p style={S.muted}>Bado hujanunua tiketi. Nenda kwenye raundi uanze.</p>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {tickets.map((t) => (
          <TicketCard key={t.ticketId} ticket={t} />
        ))}
      </div>
    </main>
  );
}

function TicketCard({ ticket }: { ticket: TicketDetail }): React.JSX.Element {
  const legs = ticket.picks.length;
  const won = ticket.grossPayoutSenti !== null && parseSenti(ticket.grossPayoutSenti) > 0n;
  const settled = ticket.settledAt !== null;

  return (
    <article style={{ ...S.card, borderColor: won ? 'var(--win)' : 'var(--border)' }}>
      <div style={{ ...S.row, marginBottom: 8 }}>
        <span style={S.muted}>{ticket.roundId}</span>
        <span style={S.muted}>
          {ticket.units}x · {formatTzs(ticket.stakeSenti)} TZS
        </span>
      </div>

      {ticket.progress === null ? (
        <div style={{ fontSize: 18, fontWeight: 700 }}>Inasubiri matokeo</div>
      ) : (
        <div style={{ fontSize: 20, fontWeight: 800 }}>
          {ticket.progress.correctLegs}/{legs}
          {!settled && ticket.progress.missedLegs > 0 && (
            <span style={{ ...S.muted, fontSize: 14, fontWeight: 400 }}>
              {' '}
              · {ticket.progress.missedLegs} zimekosewa
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', margin: '10px 0' }}>
        {ticket.picks.map((p, i) => (
          <span
            key={i}
            style={{
              width: 26,
              height: 26,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 700,
              background: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            {p === 'HOME' ? '1' : p === 'DRAW' ? 'X' : '2'}
          </span>
        ))}
      </div>

      {won && (
        <div style={{ color: 'var(--win)', fontWeight: 800, fontSize: 18 }}>
          Umeshinda {formatTzsExact(ticket.grossPayoutSenti ?? '0')} TZS
        </div>
      )}
      {settled && !won && <div style={S.muted}>Raundi imekamilika</div>}
    </article>
  );
}
